import { SplitTestsAction, SplitTestsState } from './SplitTestsState';
import { uuid } from 'utils';

export const SplitTestsReducer = (state: SplitTestsState, action: SplitTestsAction): SplitTestsState => {
  switch (action.type) {
    case 'ENABLE_SPLIT_TEST': {
      const { id, firstVariantId, secondVariantId } = action.payload;
      const newId = uuid();

      const messageCount = Object.keys(state).map(k => state[k]).length + 1;
      const defaultName = `Split test #${messageCount}`;

      return {
        ...state,
        [id]: {
          name: defaultName,
          scheduling: {
            startDate: new Date(),
            startTime: '00:00',
          },
          id,
          controlGroupEnabled: false,
          controlGroupWeight: 10,
          splitTestId: newId,
          variants: [
            { messageId: firstVariantId, name: 'A', weight: 50, isDefault: true },
            { messageId: secondVariantId, name: 'B', weight: 50, isDefault: false },
          ],
        },
      };
    }
    case 'DISABLE_SPLIT_TEST': {
      const { id } = action.payload;

      delete state[id];

      return state;
    }
    case 'REMOVE_VARIANT': {
      const { id, variantMessageId } = action.payload;

      return {
        ...state,
        [id]: { ...state[id], variants: state[id].variants.filter(s => s.messageId !== variantMessageId) },
      };
    }

    case 'UPDATE_SPLIT_TEST_ID':
      const { id, splitTestId } = action.payload;

      return {
        ...state,
        [id]: {
          ...state[id],
          splitTestId: splitTestId,
        },
      };

    case 'UPDATE_SPLIT_TEST_NAME': {
      const { id, name } = action.payload;

      return {
        ...state,
        [id]: {
          ...state[id],
          name,
        },
      };
    }

    case 'SET_DEFAULT_VARIANT': {
      const { id, variantMessageId } = action.payload;

      return {
        ...state,
        [id]: {
          ...state[id],
          variants: state[id].variants.map(variant => {
            if (variant.messageId === variantMessageId) {
              return { ...variant, isDefault: true };
            } else {
              return { ...variant, isDefault: false };
            }
          }),
        },
      };
    }

    case 'UPDATE_VARIANT_WEIGHT': {
      const { id, variantWeights } = action.payload;

      return {
        ...state,
        [id]: {
          ...state[id],
          variants: state[id].variants.map(variant => {
            const updatedWeight = variantWeights.find(w => w.messageId === variant.messageId)?.weight;
            return updatedWeight !== undefined ? { ...variant, weight: updatedWeight } : variant;
          }),
        },
      };
    }

    case 'SET_START_DATE': {
      const { id, startDate } = action.payload;
      const target = state[id];

      return {
        ...state,
        [id]: {
          ...target,
          scheduling: {
            ...target.scheduling,
            startDate,
          },
        },
      };
    }
    case 'SET_START_TIME': {
      const { id, startTime } = action.payload;
      const target = state[id];

      return {
        ...state,
        [id]: {
          ...target,
          scheduling: {
            ...target.scheduling,
            startTime,
          },
        },
      };
    }
    case 'SET_END_DATE': {
      const { id, endDate } = action.payload;
      const target = state[id];

      const defaultTime = '23:59';

      let endTime = target.scheduling.endTime ?? defaultTime;
      if (!endDate) {
        endTime = defaultTime;
      }

      return {
        ...state,
        [id]: {
          ...target,
          scheduling: {
            ...target.scheduling,
            endDate,
            endTime,
          },
        },
      };
    }
    case 'SET_END_TIME': {
      const { id, endTime } = action.payload;
      const target = state[id];

      return {
        ...state,
        [id]: {
          ...target,
          scheduling: {
            ...target.scheduling,
            endTime,
          },
        },
      };
    }
    case 'SET_SPLIT_TEST_CONTROL_GROUP': {
      const { id, controlGroupEnabled, controlGroupWeight } = action.payload;

      return {
        ...state,
        [id]: { ...state[id], controlGroupEnabled: controlGroupEnabled, controlGroupWeight: controlGroupWeight },
      };
    }
    default:
      return state;
  }
};
